<template>
  <nav class="rmr-pagination" :aria-label="ariaLabel">
    <button
      type="button"
      class="rmr-pagination__button"
      :disabled="value <= 1"
      aria-label="Page précédente"
      @click="goTo(value - 1)"
    >
      <v-icon size="16" aria-hidden="true">{{ icons.mdiChevronLeft }}</v-icon>
    </button>

    <template v-for="(token, index) in pageTokens">
      <span
        v-if="token === 'ellipsis'"
        :key="`ellipsis-${index}`"
        class="rmr-pagination__ellipsis"
        aria-hidden="true"
      >
        …
      </span>
      <button
        v-else
        :key="token"
        type="button"
        class="rmr-pagination__button"
        :class="{ 'rmr-pagination__button--active': token === value }"
        :aria-current="token === value ? 'page' : null"
        :aria-label="`Page ${token}`"
        @click="goTo(token)"
      >
        {{ token }}
      </button>
    </template>

    <button
      type="button"
      class="rmr-pagination__button"
      :disabled="value >= normalizedLength"
      aria-label="Page suivante"
      @click="goTo(value + 1)"
    >
      <v-icon size="16" aria-hidden="true">{{ icons.mdiChevronRight }}</v-icon>
    </button>
  </nav>
</template>

<script>
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

export default {
  name: "RmrPagination",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Number,
      default: 1,
    },
    length: {
      type: Number,
      default: 1,
    },
    totalVisible: {
      type: Number,
      default: 7,
    },
    ariaLabel: {
      type: String,
      default: "Pagination",
    },
  },
  data: () => ({
    icons: { mdiChevronLeft, mdiChevronRight },
  }),
  computed: {
    normalizedLength() {
      return Math.max(1, Math.floor(this.length));
    },
    pageTokens() {
      const length = this.normalizedLength;
      const requested = Math.max(5, Math.floor(this.totalVisible));
      const visible = requested % 2 === 0 ? requested - 1 : requested;
      if (length <= visible) {
        return Array.from({ length }, (_, index) => index + 1);
      }

      const current = Math.min(length, Math.max(1, this.value));
      if (current <= visible - 3) {
        return [
          ...Array.from({ length: visible - 2 }, (_, index) => index + 1),
          "ellipsis",
          length,
        ];
      }
      if (current >= length - (visible - 4)) {
        return [
          1,
          "ellipsis",
          ...Array.from(
            { length: visible - 2 },
            (_, index) => length - (visible - 3) + index
          ),
        ];
      }

      const side = Math.floor((visible - 5) / 2);
      return [
        1,
        "ellipsis",
        ...Array.from(
          { length: visible - 4 },
          (_, index) => current - side + index
        ),
        "ellipsis",
        length,
      ];
    },
  },
  methods: {
    goTo(page) {
      const next = Math.min(this.normalizedLength, Math.max(1, page));
      if (next !== this.value) this.$emit("input", next);
    },
  },
};
</script>

<style lang="scss" scoped>
.rmr-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-lg;
  background: $rmr-surface-2;
}

.rmr-pagination__button {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-0;
  color: $rmr-text-secondary;
  font-family: inherit;
  font-size: $rmr-fs-base;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled):not(.rmr-pagination__button--active) {
    border-color: $rmr-indigo-border;
    color: $rmr-indigo-dark;
  }

  &:focus-visible {
    outline: 2px solid $rmr-indigo;
    outline-offset: 2px;
  }

  &:disabled {
    border-color: $rmr-border;
    background: $rmr-surface-1;
    color: $rmr-text-subtle;
    cursor: not-allowed;
  }
}

.rmr-pagination__button--active {
  border-color: $rmr-indigo;
  background: $rmr-indigo;
  color: $rmr-surface-0;
  box-shadow: 0 1px 3px rgba(49, 46, 129, 0.2);
}

.rmr-pagination__button ::v-deep .v-icon {
  color: inherit;
}

.rmr-pagination__ellipsis {
  min-width: 18px;
  color: $rmr-text-muted;
  text-align: center;
}
</style>
